import { getDomain } from "../configs/http-config";
import { getWidgetToken } from "../utils/widgetStorage";

const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

class FileScanSSEClient {
	source = null;
	reconnectAttempts = 0;
	reconnectTimer = null;
	listeners = new Map();
	connectionParams = null;

	connect({ tenantId, contactId, botId }) {
		this.disconnect();
		this.connectionParams = { tenantId, contactId, botId };

		// No xHost/xPathname: unified-file-service reads neither (its SSE route validates
		// tenantId + contactId and takes identity from the signed widget token — file-auth.js).
		// They were carried over from chat's origin-allowlist era.
		//
		// FF-20: `botId` is not sent either — the service never reads it off this stream. It is
		// still accepted into `connect()` because the caller keys its reconnects on the same
		// params bag.
		const params = new URLSearchParams({
			tenantId,
			contactId,
		});

		// unified-file-service requires the signed widget identity (file-service B5); EventSource
		// cannot set headers, so the token rides as a query param — same pattern as the other
		// widget streams (socket-client.js / sse-client.js).
		const widgetToken = getWidgetToken();
		if (widgetToken) params.set("widgetToken", widgetToken);
		const url = `${getDomain()}/file-service/api/v1/files/sse/status?${params}`;
		this.source = new EventSource(url);

		this.source.addEventListener("file_scan_status", (e) => {
			try {
				const data = JSON.parse(e.data);
				this._notifyListeners(data);
			} catch (err) {
				console.error("FileScanSSEClient: failed to parse event", err);
			}
		});

		this.source.onopen = () => {
			this.reconnectAttempts = 0;
		};

		this.source.onerror = () => {
			this.source?.close();
			this.source = null;
			this._scheduleReconnect();
		};
	}

	disconnect() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.source) {
			this.source.close();
			this.source = null;
		}
		this.reconnectAttempts = 0;
		this.connectionParams = null;
	}

	onStatusUpdate(callback) {
		const id = Symbol();
		this.listeners.set(id, callback);
		return () => this.listeners.delete(id);
	}

	_notifyListeners(data) {
		this.listeners.forEach((cb) => {
			try { cb(data); } catch (e) { console.error("FileScanSSEClient listener error", e); }
		});
	}

	_scheduleReconnect() {
		if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
		this.reconnectAttempts++;
		this.reconnectTimer = setTimeout(() => {
			if (this.connectionParams) {
				this.connect(this.connectionParams);
			}
		}, RECONNECT_DELAY_MS * this.reconnectAttempts);
	}
}

export const fileScanSSEClient = new FileScanSSEClient();
